import React, { useState, useEffect } from 'react';
import TextInput from '../../common/TextInput';
import Textarea from '../../common/Textarea';
import { canonicalTermApi } from '../../../services/apis/configuredApis';
import './DictionaryModal.css';

function CanonicalTermModal({ show, onClose, onSave, term, graphId }) {
  const [formData, setFormData] = useState({
    code: '',
    display: '',
    description: '',
    node_id: '',
    is_active: true,
    metadata: {}
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (term) {
      setFormData({
        code: term.code || '',
        display: term.display || '',
        description: term.description || '',
        node_id: term.node_id || '',
        is_active: term.is_active !== undefined ? term.is_active : true,
        metadata: term.metadata || term.meta_data || {}
      });
    } else {
      setFormData({
        code: '',
        display: '',
        description: '',
        node_id: '',
        is_active: true,
        metadata: {}
      });
    }
    setErrors({});
    setApiError(null);
  }, [term, show]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    }
    if (!formData.display.trim()) {
      newErrors.display = 'Display is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    setApiError(null);

    try {
      // Prepare API payload - map metadata to meta_data
      const apiPayload = {
        code: formData.code.trim(),
        display: formData.display.trim(),
        description: formData.description.trim() || null,
        node_id: formData.node_id.trim() || null,
        is_active: formData.is_active,
        meta_data: formData.metadata || {}
      };

      if (term && term.id) {
        // Update existing term
        await canonicalTermApi.canonicalTermUpdateTermIdPut(term.id, apiPayload);
      } else {
        // Create new term
        await canonicalTermApi.canonicalTermCreatePost(apiPayload);
      }

      // Call onSave callback to notify parent component
      onSave(formData);
    } catch (err) {
      console.error('Error saving canonical term:', err);
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to save canonical term. Please try again.';
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="dictionary-modal-overlay" onClick={onClose}>
      <div className="dictionary-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dictionary-modal-header">
          <h2>{term ? 'Edit Canonical Term' : 'Add Canonical Term'}</h2>
          <button className="dictionary-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="dictionary-modal-body">
            <TextInput
              label="Code *"
              variant="modal"
              value={formData.code}
              onChange={(e) => handleChange('code', e.target.value)}
              placeholder="Enter code (e.g., DIAG001)"
              required
              className={errors.code ? 'error' : ''}
            />
            {errors.code && <div className="dictionary-error">{errors.code}</div>}

            <TextInput
              label="Display *"
              variant="modal"
              value={formData.display}
              onChange={(e) => handleChange('display', e.target.value)}
              placeholder="Enter display name"
              required
              className={errors.display ? 'error' : ''}
            />
            {errors.display && <div className="dictionary-error">{errors.display}</div>}

            {apiError && (
              <div className="dictionary-error" style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
                {apiError}
              </div>
            )}

            <Textarea
              label="Description"
              variant="modal"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter description"
              rows={4}
            />

            <TextInput
              label="Node ID"
              variant="modal"
              value={formData.node_id}
              onChange={(e) => handleChange('node_id', e.target.value)}
              placeholder="Enter node ID (optional)"
            />

            <div className="dictionary-form-group dictionary-form-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
                <span>Active</span>
              </label>
            </div>
          </div>
          <div className="dictionary-modal-footer">
            <button
              type="button"
              className="dictionary-btn dictionary-btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="dictionary-btn dictionary-btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (term ? 'Update' : 'Create') + ' Term'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CanonicalTermModal;

